import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Menu } from '@mui/material';
import React, { useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { wz } from '../../components/wizard-tokens';

export interface WizSelectOption {
  value: string;
  label: string;
  sub?: string;
}

interface Props {
  value: string;
  options: WizSelectOption[];
  placeholder: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
}

/** Auswahlfeld im Stil der Design-Felder (39 px, #F5F5FB, expand_more) mit echtem Menü. */
const WizSelect: React.FC<Props> = ({
  value,
  options,
  placeholder,
  onChange,
  invalid,
  disabled,
  ariaLabel,
}) => {
  const { classes, cx } = useStyles();
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);

  return (
    <>
      <button
        ref={ref}
        type="button"
        className={cx(classes.field, invalid ? 'invalid' : undefined)}
        onClick={() => setOpen(true)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className={cx(classes.label, selected ? undefined : 'placeholder')}>
          {selected ? selected.label : placeholder}
        </span>
        <ExpandMoreIcon sx={{ fontSize: 22, color: wz.primary }} />
      </button>
      <Menu
        anchorEl={ref.current}
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '10px',
              boxShadow: wz.shadowMenu,
              padding: '6px',
              minWidth: ref.current?.offsetWidth ?? 240,
            },
          },
          list: { sx: { padding: 0 } },
        }}
      >
        {options.map(o => (
          <button
            key={o.value}
            type="button"
            className={classes.item}
            onClick={() => {
              onChange(o.value);
              setOpen(false);
            }}
          >
            <span className={classes.itemText}>
              <span style={{ fontSize: 14 }}>{o.label}</span>
              {o.sub ? <span style={{ fontSize: 12, color: wz.muted }}>{o.sub}</span> : null}
            </span>
            {o.value === value ? <CheckIcon sx={{ fontSize: 18, color: wz.primary }} /> : null}
          </button>
        ))}
      </Menu>
    </>
  );
};

const useStyles = makeStyles()({
  field: {
    background: wz.page,
    borderRadius: 10,
    padding: '8px 8px 8px 12px',
    height: 39,
    boxSizing: 'border-box',
    fontSize: 15,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    fontFamily: 'inherit',
    color: wz.text,
    textAlign: 'left',
    '&.invalid': { boxShadow: `0 0 0 1px ${wz.redFg}` },
    '&:disabled': { cursor: 'default', opacity: 0.6 },
  },
  label: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&.placeholder': { color: 'rgba(28,28,28,0.5)' },
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    borderRadius: 8,
    cursor: 'pointer',
    width: '100%',
    background: 'transparent',
    border: 'none',
    fontFamily: 'inherit',
    color: wz.text,
    textAlign: 'left',
    '&:hover': { background: wz.page },
  },
  itemText: { flex: 1, display: 'flex', flexDirection: 'column', gap: 2 },
});

export default WizSelect;
